import { initializeApp } from "firebase/app";
import {
  getAuth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  sendEmailVerification,
  sendPasswordResetEmail,
  onAuthStateChanged,
  updateProfile,
} from "firebase/auth";
import { toAuthError } from "./authErrors";

/** Explicit local/dev auth — never inferred from missing env Firebase keys. */
export const explicitAuthDevMode =
  process.env.REACT_APP_AUTH_DEV_MODE === "true" ||
  process.env.REACT_APP_AUTH_DEV_MODE === "1";

let app = null;
let auth = null;
/** Updated after ensureFirebase(); live binding for any sync readers. */
export let isAuthDevMode = explicitAuthDevMode;
export let isFirebaseClientConfigured = false;

let initPromise = null;

function isUsableConfig(config) {
  return Boolean(config?.apiKey && config?.projectId);
}

/**
 * Load Firebase Web config from Hosting reserved URL (not local .env).
 * @see https://firebase.google.com/docs/hosting/reserved-urls
 */
async function fetchHostingFirebaseConfig() {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 8000);
  try {
    const res = await fetch("/__/firebase/init.json", {
      signal: controller.signal,
      credentials: "same-origin",
    });
    if (!res.ok) {
      throw new Error(`Firebase init.json returned ${res.status}`);
    }
    const contentType = res.headers.get("content-type") || "";
    if (!contentType.includes("application/json")) {
      throw new Error("Firebase init.json not available (not JSON)");
    }
    return res.json();
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Initialize Auth once. On Hosting, config comes from /__/firebase/init.json.
 * Local npm start without Hosting reserved URLs falls back to auth-dev mode
 * unless production explicitly disabled it (then callers see "not configured").
 */
export function ensureFirebase() {
  if (initPromise) return initPromise;

  initPromise = (async () => {
    if (typeof window === "undefined") {
      isAuthDevMode = true;
      isFirebaseClientConfigured = false;
      return { auth: null, isAuthDevMode: true };
    }

    // react-snap local crawl has no Hosting reserved /__/firebase/init.json
    if (typeof navigator !== "undefined" && /ReactSnap/i.test(navigator.userAgent || "")) {
      isAuthDevMode = true;
      isFirebaseClientConfigured = false;
      return { auth: null, isAuthDevMode: true };
    }

    if (explicitAuthDevMode) {
      isAuthDevMode = true;
      isFirebaseClientConfigured = false;
      return { auth: null, isAuthDevMode: true };
    }

    try {
      const config = await fetchHostingFirebaseConfig();
      if (!isUsableConfig(config)) {
        throw new Error("Firebase init.json missing apiKey or projectId");
      }
      app = initializeApp(config);
      auth = getAuth(app);
      isFirebaseClientConfigured = true;
      isAuthDevMode = false;
      return { auth, isAuthDevMode: false };
    } catch (err) {
      console.warn("Firebase Hosting config unavailable:", err?.message || err);
      isFirebaseClientConfigured = false;
      // Not explicit dev — AuthContext treats this as "not configured" for reset.
      isAuthDevMode = true;
      return { auth: null, isAuthDevMode: true };
    }
  })();

  return initPromise;
}

/** Resolves when Firebase init attempt finished (success or fallback). */
export const authReady = ensureFirebase();

export function getFirebaseAuth() {
  return auth;
}

async function requireAuth() {
  await ensureFirebase();
  if (!auth) throw new Error("Firebase is not configured");
  return auth;
}

function verificationActionCodeSettings() {
  if (typeof window === "undefined") return undefined;
  return {
    url: `${window.location.origin}/verify-email`,
    handleCodeInApp: false,
  };
}

function passwordResetActionCodeSettings() {
  if (typeof window === "undefined") return undefined;
  return {
    url: `${window.location.origin}/login`,
    handleCodeInApp: false,
  };
}

export async function firebaseSendEmailVerification(user) {
  const a = await requireAuth();
  const target = user || a.currentUser;
  if (!target) throw new Error("No signed-in user to verify");
  await sendEmailVerification(target, verificationActionCodeSettings());
}

export async function firebaseSignUp(email, password, displayName) {
  const a = await requireAuth();
  let cred;
  try {
    cred = await createUserWithEmailAndPassword(a, email, password);
  } catch (err) {
    throw toAuthError(err, "Sign up failed. Please try again.");
  }
  if (displayName) {
    await updateProfile(cred.user, { displayName });
  }
  // Verification email is sent by our backend (branded, via Resend) after sync,
  // not by Firebase's default sender. See AuthContext.signUp.
  return cred.user;
}

/** Reload Firebase user + force-refresh ID token so email_verified claim is current. */
export async function firebaseReloadAndRefreshToken() {
  await requireAuth();
  if (!auth?.currentUser) throw new Error("No signed-in user");
  await auth.currentUser.reload();
  await auth.currentUser.getIdToken(true);
  return auth.currentUser;
}

export async function firebaseSignIn(email, password) {
  const a = await requireAuth();
  try {
    const cred = await signInWithEmailAndPassword(a, email, password);
    return cred.user;
  } catch (err) {
    throw toAuthError(err, "Sign in failed. Please try again.");
  }
}

export async function firebaseSendPasswordResetEmail(email) {
  const a = await requireAuth();
  await sendPasswordResetEmail(a, email, passwordResetActionCodeSettings());
}

export async function firebaseSignOut() {
  await ensureFirebase();
  if (!auth) return;
  await signOut(auth);
}

export function subscribeAuth(callback) {
  if (!auth) {
    callback(null);
    return () => {};
  }
  return onAuthStateChanged(auth, callback);
}

export async function getIdToken(forceRefresh = false) {
  await ensureFirebase();
  if (!auth?.currentUser) return null;
  return auth.currentUser.getIdToken(forceRefresh);
}

/** Dev-mode token compatible with backend AUTH_DEV_MODE */
export function makeDevToken({ uid, email, name, email_verified = true }) {
  const payload = JSON.stringify({
    uid,
    email,
    name: name || "",
    email_verified,
  });
  const b64 =
    typeof window !== "undefined" && window.btoa
      ? window
          .btoa(unescape(encodeURIComponent(payload)))
          .replace(/\+/g, "-")
          .replace(/\//g, "_")
          .replace(/=+$/, "")
      : Buffer.from(payload).toString("base64url");
  return `dev.${b64}`;
}
